/**
 * One scenario's run read as a conversation, drawn with the simulator thread's primitives.
 * While a scenario runs the server sends one `item.step` label, overwritten each time; the
 * page gathers them into a lossy trail of milestones, never a complete timeline. Everything
 * richer (reply, tool calls, verdict, investigation) arrives once the item settles.
 */

import { useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Bot, ExternalLink, GitCompareArrows, Loader2, Radio, ShieldAlert, UserRound, Wrench } from 'lucide-react';
import { formatDuration } from '@/lib/duration';
import { RunReportDialog } from '@/modules/review/report/run-report-dialog';
import { readVerdictReport } from '@/modules/review/report-data';
import { VerdictDisclosure } from '@/modules/review/verdict-disclosure';
import { investigationStatus, missingPhrases, num, rec, str } from '@/modules/review/verdict-readers';
import { comparedReplyView } from '@/modules/simulator/review/compared-reply-view';
import { GapInvestigationView } from '@/modules/simulator/review/gap-investigation-view';
import { ReportViews } from '@/modules/simulator/review/report-views';
import { useRunReview } from '@/modules/simulator/review/use-run-review';
import { useReplySide } from '@/modules/simulator/review/reply-side';
import { StepIO } from '@/modules/simulator/ui/step-io';
import { StepRail, type RailStep } from '@/modules/simulator/ui/step-rail';
import { ToolChip, ToolProvenance } from '@/modules/simulator/ui/tool-provenance';
import { ThreadDisclosure } from '@/modules/simulator/ui/thread-disclosure';
import { TurnBubble, TurnLabel, TurnRow } from '@/modules/simulator/ui/turn';
import { threadText } from '../format';
import { replayLine, replayWhy } from '../recording/replay';
import type { RunFacts } from './run-facts';
import type { BulkRunDetail, BulkRunToolCall } from '@/services/ai-function.service';

/** A turn in the thread, with its author line. */
const Row = ({
	side,
	icon,
	iconClassName,
	label,
	labelClassName,
	children
}: {
	side: 'left' | 'right';
	icon: LucideIcon;
	iconClassName?: string;
	label: ReactNode;
	labelClassName?: string;
	children: ReactNode;
}) => (
	<TurnRow side={side}>
		<TurnLabel icon={icon} iconClassName={iconClassName} className={labelClassName}>
			<span className='font-medium'>{label}</span>
		</TurnLabel>
		{children}
	</TurnRow>
);

/** One authored turn of the scenario, in the thread's own bubble. */
export const Turn = ({ role, text }: { role: 'user' | 'assistant'; text: string }) => {
	const isCustomer = role === 'user';
	return (
		<Row side={isCustomer ? 'left' : 'right'} icon={isCustomer ? UserRound : Bot} label={isCustomer ? 'Customer' : 'Agent'}>
			<TurnBubble tone={isCustomer ? 'customer' : 'agent'} className='whitespace-pre-wrap wrap-break-word'>
				{threadText(text)}
			</TurnBubble>
		</Row>
	);
};

const AiReply = ({ text }: { text: string }) => (
	<Row side='right' icon={Bot} iconClassName='text-indigo-500' label='AI · Agent' labelClassName='text-indigo-600'>
		<TurnBubble tone='ai' className='whitespace-pre-wrap wrap-break-word'>
			{text}
		</TurnBubble>
	</Row>
);

/** The AI reply with the human agent's reply switchable in; a bulk run never writes a reference reply of its own. */
const ComparedAiReply = ({ requestId, detail }: { requestId: string; detail: BulkRunDetail }) => {
	const side = useReplySide(requestId);
	const view = comparedReplyView(side, {
		requestId,
		aiReply: detail.response,
		agentReply: detail.realResponse,
		similarity: num(detail.comparison?.['semanticSimilarity']),
		authored: false,
		highlights: missingPhrases(detail.comparison)
	});
	return (
		<TurnRow side='right'>
			<TurnLabel icon={view.icon} iconClassName='text-indigo-500'>
				{view.label}
			</TurnLabel>
			<TurnBubble tone='ai' className='wrap-break-word'>
				{view.body}
			</TurnBubble>
		</TurnRow>
	);
};

/** Something that happened instead of, or alongside, a reply. */
const Notice = ({
	tone,
	icon,
	title,
	children
}: {
	tone: 'error' | 'warning';
	icon: LucideIcon;
	title: string;
	children: ReactNode;
}) => (
	<Row side='right' icon={icon} label={title} labelClassName={tone === 'error' ? 'text-red-600' : 'text-amber-600'}>
		<TurnBubble tone={tone}>{children}</TurnBubble>
	</Row>
);

const Fold = ({ children }: { children: ReactNode }) => (
	<div className='mt-2 flex flex-col gap-2 rounded-xl border border-gray-500 bg-gray-50/60 px-3.5 py-3'>{children}</div>
);

/** The run's tool calls as steps on the rail. */
const toolCallSteps = (toolCalls: BulkRunToolCall[]): RailStep[] =>
	toolCalls.map((call, index) => ({
		key: `${call.name}-${index}`,
		icon: Wrench,
		tone: call.outcome === 'error' ? 'text-red-500' : 'text-amber-500',
		label: (
			<>
				<span className='font-mono'>{call.name}</span>
				{/* A stored call does not record whether production asks a human first, so every simulated write is marked gated. */}
				<ToolProvenance
					simulated={call.simulated}
					grounded={call.grounded}
					simulatedWrite={call.simulatedWrite}
					recorded={call.recorded}
					why={replayWhy(call)}
					gated={call.simulatedWrite}
				/>
				{call.outcome === 'error' && <ToolChip className='border-red-200 bg-red-50 text-red-700'>error</ToolChip>}
				{call.outcome === 'awaiting_approval' && (
					<ToolChip className='border-blue-200 bg-blue-50 text-blue-700'>awaiting approval</ToolChip>
				)}
			</>
		),
		meta: call.durationMs !== null ? formatDuration(call.durationMs) : undefined,
		detail: <StepIO name={call.name} input={call.args} output={call.output} simulated={call.simulated} />
	}));

interface RunThreadProps {
	run: RunFacts;
	/** The persisted run, once the item has settled and the fetch has landed. */
	detail: BulkRunDetail | undefined;
	/** The scenario's turns, available from the pool before any detail exists. */
	queryTurns: BulkRunDetail['query'] | null;
	/** The bulk run itself is still going. */
	active: boolean;
	/** Milestones caught for this item while it ran; lossy. */
	trail: string[];
	/** Live investigation milestones for this run, before its verdict lands. */
	investigationTrail: string[] | undefined;
	/** Which fold is open; owned above so it survives switching between scenarios. */
	verdictOpen: boolean;
	onVerdictOpenChange: (open: boolean) => void;
	reportOpen: boolean;
	onReportOpenChange: (open: boolean) => void;
	/** Off where every run on screen shares one already-visible query. */
	showTurns?: boolean;
}

export const RunThread = ({
	run,
	detail,
	queryTurns,
	active,
	trail,
	investigationTrail,
	verdictOpen,
	onVerdictOpenChange,
	reportOpen,
	onReportOpenChange,
	showTurns = true
}: RunThreadProps) => {
	const [milestonesOpen, setMilestonesOpen] = useState(true);
	const [toolsOpen, setToolsOpen] = useState(false);
	const turns = showTurns ? (detail?.query ?? queryTurns ?? []) : [];
	const comparison = detail?.comparison ?? null;
	const review = useRunReview(run.requestId ?? '');
	const local = review.investigation;
	const envelope = local.status === 'done' ? local.investigation : detail?.investigation;
	const verdict = readVerdictReport(envelope);
	const investigation = investigationStatus(envelope);
	const investigating = local.status === 'running' || investigation === 'pending';
	const failure = local.status === 'failed' ? local.reason : investigation === 'failed' ? str(rec(envelope)?.['reason']) ?? 'error' : null;
	const steps = local.status === 'running' ? local.steps : (investigationTrail ?? []).map((text) => ({ text, ts: null }));
	const running = active && run.status === 'running';
	const toolCalls = detail?.toolCalls ?? [];
	const replay = detail?.recording ? replayLine(detail.recording) : null;
	const investigate = () => {
		if (!run.requestId || !detail?.langfuseTraceUrl || investigating) return;
		void review.runInvestigation({ langfuseTraceUrl: detail.langfuseTraceUrl, bucket: run.bucket, ticketId: detail.ticketId });
	};

	return (
		<div className='flex flex-col gap-4 px-1 py-2'>
			{turns.map((turn, index) => (
				<Turn key={index} role={turn.role} text={turn.text} />
			))}

			{/* "Milestones", not a step count: a count would claim a completeness the server does not offer. */}
			{(running || trail.length > 0) && !detail?.response && (
				<ThreadDisclosure
					icon={running ? Loader2 : Radio}
					iconClassName={running ? 'animate-spin text-indigo-500' : undefined}
					label={running ? 'Running…' : 'Milestones'}
					meta={trail.length > 0 ? <span className='opacity-70'>{trail.length} seen</span> : undefined}
					active={running}
					open={milestonesOpen}
					onToggle={() => setMilestonesOpen((prev) => !prev)}
					contentClassName='max-w-[480px]'
				>
					<Fold>
						{trail.length > 0 ? (
							<StepRail
								steps={trail.map((step, index) => ({
									key: `${step}-${index}`,
									icon: Radio,
									tone: 'text-indigo-600',
									running: running && index === trail.length - 1,
									label: step
								}))}
							/>
						) : (
							<p className='text-xs leading-4.5 text-gray-800'>Waiting for the first milestone…</p>
						)}
					</Fold>
				</ThreadDisclosure>
			)}

			{run.status === 'failed' && (
				<Notice tone='error' icon={ShieldAlert} title='This scenario did not finish'>
					<p className='whitespace-pre-wrap'>{run.error ?? 'The run stopped without saying why.'}</p>
				</Notice>
			)}

			{/* A protocol path can end by handing the ticket to a person: a finished run for a reviewer to mark. */}
			{run.status === 'done' && run.escalation && (
				<Row side='right' icon={UserRound} label='Handed off to a person' labelClassName='text-gray-800'>
					<TurnBubble tone='customer' className='whitespace-pre-wrap wrap-break-word'>
						{run.escalation}
					</TurnBubble>
				</Row>
			)}

			{detail?.error && run.status !== 'failed' && (
				<Notice tone='error' icon={ShieldAlert} title='The run reported an error'>
					<p className='whitespace-pre-wrap'>{detail.error}</p>
				</Notice>
			)}

			{detail?.response &&
				(detail.realResponse.length > 0 && run.requestId ? (
					<ComparedAiReply requestId={run.requestId} detail={detail} />
				) : (
					<AiReply text={detail.response} />
				))}

			{run.status === 'queued' && <p className='px-1 text-xs leading-4 text-gray-800'>Waiting its turn…</p>}
			{run.status === 'running' && !active && (
				<p className='px-1 text-xs leading-4 text-gray-800'>The run stopped before this scenario finished.</p>
			)}

			{(toolCalls.length > 0 || detail?.langfuseTraceUrl || replay) && (
				<ThreadDisclosure
					icon={Wrench}
					label='Tool calls'
					meta={
						toolCalls.length > 0 || replay ? (
							<span className='opacity-70' title={replay?.title}>
								{[toolCalls.length || null, replay?.label].filter(Boolean).join(' · ')}
							</span>
						) : undefined
					}
					open={toolsOpen}
					onToggle={() => setToolsOpen((prev) => !prev)}
					contentClassName='max-w-[480px]'
				>
					<Fold>
						{toolCalls.length > 0 ? (
							<StepRail steps={toolCallSteps(toolCalls)} />
						) : (
							<p className='text-xs leading-4.5 text-gray-800'>No tool was called.</p>
						)}
						{detail?.langfuseTraceUrl && (
							<a
								href={detail.langfuseTraceUrl}
								target='_blank'
								rel='noreferrer'
								className='inline-flex w-fit items-center gap-1 text-[11px] text-indigo-700 transition-colors hover:text-indigo-900'
							>
								Langfuse trace
								<ExternalLink className='size-3' aria-hidden />
							</a>
						)}
					</Fold>
				</ThreadDisclosure>
			)}

			{detail?.comparisonError && (
				<Notice tone='warning' icon={GitCompareArrows} title='The judge could not run'>
					<p className='whitespace-pre-wrap'>{detail.comparisonError}</p>
				</Notice>
			)}

			{detail && (detail.response || detail.langfuseTraceUrl || detail.investigation) && (
				<ReportViews key={run.requestId} shadow={comparison ? (
					<VerdictDisclosure comparison={comparison} bucket={run.bucket} open={verdictOpen}
						aiReply={detail.response} humanReply={detail.realResponse.join('\n\n')}
						onToggle={() => onVerdictOpenChange(!verdictOpen)} />
				) : (
					<p className='text-xs leading-4 text-gray-800'>
						{detail.comparisonError ? `The Shadow comparison could not run: ${detail.comparisonError}` :
							'No Shadow comparison is available for this run. Gap investigation only requires a trace.'}
					</p>
				)} gap={
					<GapInvestigationView investigation={envelope} running={investigating} steps={steps} failure={failure}
						canInvestigate={Boolean(run.requestId && detail.langfuseTraceUrl)} onInvestigate={investigate}
						onOpenReport={() => onReportOpenChange(true)} />
				} />
			)}

			{verdict && (
				<RunReportDialog open={reportOpen} onOpenChange={onReportOpenChange} investigation={envelope} />
			)}
		</div>
	);
};
